import { inject, Injectable } from '@angular/core';
import { BaseFlowsStore } from '@kinet-open-network/client/flows/data-access';
import {
  loadItemPrices,
  LoadItemPricesMode,
  ProductPricesService,
} from '@kinet-open-network/client/product-prices/data-access';
import { OrderStatus } from '@kinet-open-network/shared/data-access/model';
import { sumTotals } from '@kinet-open-network/shared/util';
import {
  FlowGoodsReceipts,
  FlowGoodsReceiptsStepData,
} from './flow-goods-receipts.model';
import { FlowGoodsReceiptsService } from './flow-goods-receipts.service';

@Injectable()
export class FlowGoodsReceiptsStore extends BaseFlowsStore<
  FlowGoodsReceipts,
  'id',
  FlowGoodsReceiptsStepData
>(FlowGoodsReceiptsService) {
  override readonly service = inject(FlowGoodsReceiptsService);
  private readonly productPricesService = inject(ProductPricesService);

  override isTerminalState(status: string): boolean {
    return status === OrderStatus.delivered;
  }

  /** Reloads item prices for the receipt's supplier and date; returns whether any price changed. */
  async loadPrices(mode: LoadItemPricesMode): Promise<boolean> {
    const model = this.model();
    if (!model?.items?.length) return false;

    const { items, changed } = await loadItemPrices(model.items, {
      service: this.productPricesService,
      customerId: model.customerId ?? null,
      date: model.date ? new Date(model.date) : new Date(),
      mode,
      // only applies to `effective` mode: same rule as the lookup on stock item selection (default prices are ignored)
      allowedSources: ['tag', 'customer'],
    });

    if (changed) {
      this.patchModel({
        items,
        ...sumTotals(items),
      } as Partial<FlowGoodsReceipts>);
    }
    return changed;
  }
}
